import {
  ContextMenu,
  IconName,
  InputContainer,
  Theme,
} from '@infotech-soe/erp-ui-kit';
import { DbId } from '@infotech-soe/metaschema';
import cytoscape, { EdgeDefinition, InputEventObject } from 'cytoscape';
import dblclick from 'cytoscape-dblclick';
import klay from 'cytoscape-klay';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'react-jss';

import { IGraphData } from '../../../store/slices/graph';

import { DocLinksGraphDialogContainerProps } from './DocLinksGraphDialogContainer';
import { graphMessages as messages } from './messages';
import { getCytoscapeStyles, useGraphStyles } from './styles';
import {
  ICategoryNodeData,
  IContextMenu,
  ICytoProps,
  IDocLinksGraphNode,
  NodeType,
} from './types';

cytoscape.use(klay);
cytoscape.use(dblclick);

interface DocLinksGraphProps extends Pick<
  DocLinksGraphDialogContainerProps,
  'data' | 'onItemOpen' | 'onRebuildEdges' | 'docId'
> {
  cyto?: ICytoProps;
  needsRefresh: number;
}

const defaultCytoProps: ICytoProps = {
  layout: { name: 'klay' },
  minZoom: 0.3,
  maxZoom: 1,
  zoom: 1,
  ready(event: cytoscape.EventObject) {
    event.cy.nodes('[type = "categoryNode"]').forEach(parentNode => {
      const children = parentNode.children();
      for (let i = 1; i < children.length; i++) {
        const prev = children[i - 1];
        const curr = children[i];
        curr.relativePosition({
          x: prev.relativePosition().x,
          y: prev.relativePosition().y + prev.outerHeight() - 1,
        });
      }
    });
  },
};

const DocLinksGraph: React.FC<DocLinksGraphProps> = ({
  docId,
  data,
  needsRefresh,
  onItemOpen,
  onRebuildEdges,
  cyto: cytoProps = defaultCytoProps,
}) => {
  const classes = useGraphStyles();
  const theme = useTheme<Theme>();
  const { formatMessage } = useIntl();
  const graphRef = useRef<HTMLDivElement>(null);
  const cytoRef = useRef<cytoscape.Core>();
  const [contextMenu, setContextMenu] = useState<IContextMenu>({
    open: false,
  });
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  const nodesRef = useRef<IDocLinksGraphNode[]>([]);
  const edgesRef = useRef<EdgeDefinition[]>([]);

  const cyStyles = useMemo(() => getCytoscapeStyles(theme), [theme]);

  const convertData = useCallback(() => {
    const nodes: IDocLinksGraphNode[] = [];
    const edges: EdgeDefinition[] = [];
    const categoryGroups: Record<string, IGraphData[]> = {};
    const docIdToCategory: Record<DbId, string> = {};

    data.forEach(element => {
      const catName = element.category;
      if (!categoryGroups[catName]) {
        categoryGroups[catName] = [];
      }
      categoryGroups[catName].push(element);
      docIdToCategory[element.id] = catName;
    });

    const createCategoryNode = (categoryNodeId: string, categoryName: string) =>
      nodes.push({
        data: {
          type: NodeType.CATEGORY,
          id: categoryNodeId,
          category: categoryName,
        },
      });

    const createHeaderNode = (parent: string, label: string) =>
      nodes.push({
        data: {
          type: NodeType.HEADER,
          parent,
          label,
        },
      });

    const createDocumentNode = (parent: string, doc: IGraphData) =>
      nodes.push({
        data: {
          type: NodeType.DOCUMENT,
          parent,
          id: doc.id.toString(),
          label: doc.description || formatMessage(messages.description),
        },
      });

    const createSummaryNode = (
      parent: string,
      docIds: number[],
      label: string
    ) =>
      nodes.push({ data: { type: NodeType.SUMMARY, parent, docIds, label } });

    Object.entries(categoryGroups).forEach(([categoryName, docs]) => {
      const categoryLabel = docs[0].label;

      if (expandedCategories.has(categoryName)) {
        docs.forEach(doc => {
          const categoryNodeId = `${categoryName}-${doc.id}`;
          createCategoryNode(categoryNodeId, categoryName);
          createHeaderNode(categoryNodeId, categoryLabel);
          createDocumentNode(categoryNodeId, doc);
        });
      } else {
        createCategoryNode(categoryName, categoryName);
        createHeaderNode(categoryName, categoryLabel);
        if (docs.length === 1) {
          createDocumentNode(categoryName, docs[0]);
        } else {
          const docIds = docs.map(doc => doc.id);
          const summaryLabel = formatMessage(messages.documentsCount, {
            count: docIds.length,
          });
          createSummaryNode(categoryName, docIds, summaryLabel);
        }
      }
    });

    data.forEach(element => {
      const sourceCategory = element.category;
      const isSourceExpanded = expandedCategories.has(sourceCategory);
      const sourceNodeId = isSourceExpanded
        ? `${sourceCategory}-${element.id}`
        : sourceCategory;

      element.outEdges.forEach(targetDocId => {
        const targetCategory = docIdToCategory[targetDocId];
        if (targetCategory) {
          const isTargetExpanded = expandedCategories.has(targetCategory);
          const targetNodeId = isTargetExpanded
            ? `${targetCategory}-${targetDocId}`
            : targetCategory;

          if (sourceNodeId !== targetNodeId) {
            const edgeExists = edges.some(
              e =>
                e.data.source === sourceNodeId && e.data.target === targetNodeId
            );
            if (!edgeExists) {
              edges.push({
                data: {
                  source: sourceNodeId,
                  target: targetNodeId,
                },
              });
            }
          }
        }
      });
    });

    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [data, formatMessage, expandedCategories]);

  const handleOnItemOpen = useCallback(
    (categoryName: string, categoryNodeId: string) => {
      const summaryNode = nodesRef.current.find(
        ({ data: { type, parent } }) =>
          type === NodeType.SUMMARY && parent === categoryNodeId
      );

      const docIds = summaryNode
        ? summaryNode.data.docIds
        : nodesRef.current
            .filter(
              ({ data: { type, parent } }) =>
                type === NodeType.DOCUMENT && parent === categoryNodeId
            )
            .map(node => Number(node.data.id));

      onItemOpen(categoryName, docIds);
    },
    [onItemOpen]
  );

  const handleOnItemCxt = useCallback(
    (event: InputEventObject) => {
      const { id: nodeId, category: categoryName } =
        event.target.data() as ICategoryNodeData;

      const summaryNode = nodesRef.current.find(
        ({ data: { type, parent } }) =>
          parent === nodeId && type === NodeType.SUMMARY
      );

      const documents = nodesRef.current.filter(
        ({ data: { type, parent } }) =>
          parent === nodeId && type === NodeType.DOCUMENT
      );

      const docCount = summaryNode
        ? summaryNode.data.docIds.length
        : documents.length;
      const isSrcNode = documents.some(doc => Number(doc.data.id) === docId);

      setContextMenu({
        open: true,
        x: event.originalEvent.clientX,
        y: event.originalEvent.clientY,
        categoryName,
        categoryNodeId: nodeId,
        canExpanded: docCount > 1 && !expandedCategories.has(categoryName),
        canCollapse: expandedCategories.has(categoryName),
        canChangeEdges: !isSrcNode && docCount === 1,
      });
    },
    [expandedCategories, docId]
  );

  const initCyto = useCallback(() => {
    if (!graphRef.current) {
      return;
    }
    const handleOnItemCxtDblClick = (event: InputEventObject) => {
      const { id, category } = event.target.data() as ICategoryNodeData;

      handleOnItemOpen(category, id);
    };

    cytoRef.current = cytoscape({
      container: graphRef.current,
      layout: cytoProps?.layout,
      minZoom: cytoProps?.minZoom,
      maxZoom: cytoProps?.maxZoom,
      zoom: cytoProps?.zoom,
      style: cyStyles,
      elements: {
        nodes: nodesRef.current,
        edges: edgesRef.current,
      },
    });
    cytoRef.current.nodes('[type="categoryNode"]').forEach(node => {
      node.on('cxttap', handleOnItemCxt);
      node.on('dblclick', handleOnItemCxtDblClick);
    });

    cytoRef.current.center();
    cytoRef.current.ready(event => {
      cytoProps?.ready?.(event);
      const sourceDocNode = event.cy
        .nodes(`[type = "documentNode"][id = "${docId}"]`)
        .first();
      if (sourceDocNode.length > 0) {
        const parentNode = sourceDocNode.parent();
        if (parentNode.length > 0) {
          parentNode.addClass('source');
          parentNode.select();
        }
      }
    });
  }, [cyStyles, cytoProps, docId, handleOnItemCxt, handleOnItemOpen]);

  useEffect(() => {
    convertData();
    initCyto();
    return () => {
      cytoRef.current?.destroy();
      cytoRef.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, expandedCategories]);

  useEffect(() => {
    const handleOnGraphCxtTap = (event: InputEventObject) => {
      if (event.target.constructor.name === 'Core') {
        setContextMenu({ open: false });
      }
    };

    cytoRef.current?.on('tapstart', handleOnGraphCxtTap);
    cytoRef.current?.on('cxttap', handleOnGraphCxtTap);
    return () => {
      cytoRef.current?.off('tapstart', handleOnGraphCxtTap);
      cytoRef.current?.off('cxttap', handleOnGraphCxtTap);
    };
  }, []);

  const handleOnContextMenuClick = useCallback(
    (name: string) => {
      const { categoryName, categoryNodeId } = contextMenu;
      setContextMenu({ open: false });

      if (!categoryName || !categoryNodeId) return;

      if (name === 'open') {
        handleOnItemOpen(categoryName, categoryNodeId);
      } else if (name === 'change-edges') {
        const documents = nodesRef.current.filter(
          ({ data: { type, parent } }) =>
            parent === categoryNodeId && type === NodeType.DOCUMENT
        );
        if (documents.length > 0) {
          onRebuildEdges(categoryName, Number(documents[0].data.id));
        }
      } else if (name === 'expand') {
        setExpandedCategories(prev => new Set(prev).add(categoryName));
      } else if (name === 'collapse') {
        setExpandedCategories(prev => {
          const next = new Set(prev);
          next.delete(categoryName);
          return next;
        });
      }
    },
    [contextMenu, handleOnItemOpen, onRebuildEdges]
  );

  const menuOptions = useMemo(
    () => [
      {
        type: 'listitem',
        value: 'open',
        label: formatMessage(messages.open),
        icon: 'doclinks-open' as IconName,
      },
      {
        type: 'listitem',
        value: 'change-edges',
        label: formatMessage(messages.changeEdges),
        icon: 'doclinks-rebuild-edges' as IconName,
        disabled: !contextMenu.canChangeEdges,
      },
      { type: 'separator', value: 'separator' },
      {
        type: 'listitem',
        value: 'expand',
        label: formatMessage(messages.expand),
        icon: 'plan-org-change' as IconName,
        disabled: !contextMenu.canExpanded,
      },
      {
        type: 'listitem',
        value: 'collapse',
        label: formatMessage(messages.collapse),
        icon: 'plan-org-close' as IconName,
        disabled: !contextMenu.canCollapse,
      },
    ],
    [
      formatMessage,
      contextMenu.canExpanded,
      contextMenu.canCollapse,
      contextMenu.canChangeEdges,
    ]
  );

  const handleOnGraphWheel = () => {
    setContextMenu({ open: false });
  };

  useEffect(() => {
    cytoRef.current?.resize();
    cytoRef.current?.center();
    cytoRef.current?.fit();
  }, [needsRefresh]);

  return (
    <InputContainer
      className={classes.root}
      onContextMenu={e => e.preventDefault()}
      tabIndex={0}
    >
      <div
        className={classes.graph}
        onWheel={handleOnGraphWheel}
        ref={graphRef}
      />
      <ContextMenu
        open={contextMenu.open}
        x={contextMenu.x}
        y={contextMenu.y}
        onClick={handleOnContextMenuClick}
        onOpen={() => setContextMenu({ open: false })}
        options={menuOptions}
      />
    </InputContainer>
  );
};

export default DocLinksGraph;
